import { useEffect, useState } from 'react';
import { numberError } from '../config/ConfigKeyEditor';
import ConfigHistoryDialog from '../config/ConfigHistoryDialog';
import { useConfigCard } from '../config/useConfigCard';
import { CardStateFrame, ConfigCardFrame, ConfigSwitch, ForbiddenNotice, money, NumberField } from './ConfigCardParts';
import ConfirmChangesDialog, { ChangeLine } from './ConfirmChangesDialog';
import { TABBY_DEFAULTS, TABBY_KEYS } from './financeConfigDefaults';

const K = TABBY_KEYS;
const KEYS = Object.values(TABBY_KEYS);

const LABELS: Record<string, string> = {
  [K.customerEnabled]: 'Tabby for customers',
  [K.pilotCustomerIds]: 'Pilot customers',
  [K.minOrderAmount]: 'Minimum order',
  [K.feePercent]: 'Tabby fee percent',
  [K.feeFixedAed]: 'Tabby fixed fee',
  [K.feeVatInclusive]: 'Fee includes VAT',
  [K.feeBearer]: 'Paid by',
  [K.feeGarageBase]: 'Garage pays it on',
  [K.settlementHoldDays]: 'Settlement hold',
  [K.payoutFeeAed]: 'Tabby payout fee',
};

const BEARER_LABELS: Record<string, string> = { GARAGE: 'Garage (deducted from its payout)', GARAGAT: 'Garagat (we absorb it)' };
const BASE_LABELS: Record<string, string> = { FULL_PAYMENT: 'The full payment', SERVICE_ONLY: 'Its service share only' };

export const CLEAR_FAILED = 'Your other changes were saved, but the pilot list was not cleared. Empty it and save again.';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The pilot list as ids: commas, spaces and new lines all separate. */
export function pilotIds(raw: string | undefined): string[] {
  return (raw ?? '').split(/[\s,]+/).filter(Boolean);
}

/** The same rule the server's pattern applies (customer ids, comma separated), so most typos never leave the card. */
export function pilotError(raw: string): string | null {
  const bad = pilotIds(raw).find((id) => !UUID.test(id));
  return bad ? `Not a customer id: ${bad.slice(0, 24)}. Use customer ids separated by commas.` : null;
}

const num = (v: string | undefined) => {
  const n = Number((v ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};
const countLabel = (n: number) => (n ? `${n} customer${n > 1 ? 's' : ''}` : 'Every customer');

/** Options in a fixed, readable order, limited to what the key accepts (its `allowedValues`). */
function options(allowed: string[] | undefined, labels: Record<string, string>) {
  const known = Object.keys(labels);
  const values = allowed && allowed.length ? known.filter((v) => allowed.includes(v)).concat(allowed.filter((v) => !known.includes(v))) : known;
  return values.map((v) => ({ value: v, label: labels[v] ?? v }));
}

const NUMBER_FIELDS = [K.minOrderAmount, K.feePercent, K.feeFixedAed, K.settlementHoldDays, K.payoutFeeAed] as const;
const IN_GROUP: string[] = [K.feePercent, K.feeFixedAed, K.settlementHoldDays, K.payoutFeeAed];

/**
 * #1113 — Finance → Configuration: Tabby ("Pay later with Tabby" at checkout, Spec 067) and what
 * Tabby charges us for it. The ten Tabby card keys are saved in one atomic request (all or none), so a
 * fee contract is never half-applied to the Tabby payments captured meanwhile. Flipping the customer
 * switch always asks first. The History dialog masks the pilot customer ids.
 *
 * <p>An empty pilot list cannot be stored (the key's pattern needs at least one id), so emptying it
 * reverts the key to its deployed default (no list = every customer), after the rest is saved.
 */
export default function TabbyCard({ token }: { token: string }) {
  const card = useConfigCard({ keys: KEYS, screen: 'finance-config', defaults: TABBY_DEFAULTS, token });
  const { draft, server, entries } = card;
  const [groupOpen, setGroupOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState(false);

  // A server 400 on a fee or payout field opens the group so the message is seen.
  useEffect(() => {
    if (Object.keys(card.fieldErrors).some((k) => IN_GROUP.includes(k))) setGroupOpen(true);
  }, [card.fieldErrors]);

  if (card.load.kind === 'loading') {
    return (
      <CardStateFrame testId="tabby-card" title="Tabby">
        <div className="admin-loading" aria-live="polite"><div className="admin-loading-spinner" /></div>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'error') {
    return (
      <CardStateFrame testId="tabby-card" title="Tabby">
        <div className="admin-info-banner danger" role="alert" data-testid="tabby-load-error">{card.load.message}</div>
        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={() => card.reload()}>Try again</button>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'missing') {
    return (
      <CardStateFrame testId="tabby-card" title="Tabby">
        <div className="admin-info-banner warning" role="status" data-testid="tabby-unavailable">
          The Tabby settings are not available to your account on this environment. Reload the page, or ask a super admin.
        </div>
      </CardStateFrame>
    );
  }

  const v = (key: string) => draft[key] ?? '';
  const errorOf = (key: string): string | null => {
    if (card.fieldErrors[key]) return card.fieldErrors[key];
    if (key === K.pilotCustomerIds) return pilotError(v(key));
    const e = entries.get(key);
    return e && (NUMBER_FIELDS as readonly string[]).includes(key) ? numberError(e, v(key), { whole: key === K.settlementHoldDays }) : null;
  };
  const invalid = [K.pilotCustomerIds, ...NUMBER_FIELDS].filter((k) => errorOf(k));
  const groupInvalid = invalid.some((k) => IN_GROUP.includes(k));

  // The pilot list compares as ids, so re-spacing it is not a change; an emptied list is a revert.
  const draftIds = pilotIds(v(K.pilotCustomerIds));
  const serverIds = pilotIds(server[K.pilotCustomerIds]);
  const sameIds = draftIds.join(',') === serverIds.join(',');
  const effective: Record<string, string> = {
    ...draft,
    [K.pilotCustomerIds]: sameIds ? server[K.pilotCustomerIds] : draftIds.join(', '),
  };
  const clearPilot = !sameIds && draftIds.length === 0 && !!entries.get(K.pilotCustomerIds)?.overridden;
  const changes = card.changesFrom(effective).filter((c) => !(clearPilot && c.key === K.pilotCustomerIds));
  const clears = clearPilot ? [K.pilotCustomerIds] : [];
  const dirty = changes.length > 0 || clears.length > 0;

  const enabled = v(K.customerEnabled) === 'true';
  const serverEnabled = server[K.customerEnabled] === 'true';
  const garagePays = v(K.feeBearer) === 'GARAGE';
  const vatIncl = v(K.feeVatInclusive) === 'true';
  const deployed = (key: string) => !entries.get(key)?.overridden;
  const range = (key: string) => {
    const e = entries.get(key);
    return e && e.min !== null && e.max !== null ? `${e.min}–${e.max}` : '';
  };

  const status = !serverEnabled
    ? { text: 'Off for customers', cls: 'neutral' }
    : serverIds.length
      ? { text: `On · pilot, ${countLabel(serverIds.length).toLowerCase()}`, cls: 'pending' }
      : { text: 'On for every customer', cls: 'approved' };

  const fmt = (key: string, value: string) => {
    switch (key) {
      case K.customerEnabled:
      case K.feeVatInclusive: return value === 'true' ? 'On' : 'Off';
      case K.pilotCustomerIds: return countLabel(pilotIds(value).length);
      case K.feeBearer: return value === 'GARAGE' ? 'Garage' : 'Garagat';
      case K.feeGarageBase: return value === 'SERVICE_ONLY' ? 'Service share only' : 'Full payment';
      case K.feePercent: return `${num(value)}%`;
      case K.settlementHoldDays: return `${num(value)} days`;
      default: return `AED ${money(num(value))}`;
    }
  };
  const changeLines = (): ChangeLine[] => [
    ...changes.map((c) => ({ label: LABELS[c.key], from: fmt(c.key, server[c.key]), to: fmt(c.key, c.value) })),
    ...clears.map((k) => ({ label: LABELS[k], from: fmt(k, server[k]), to: 'Every customer' })),
  ];

  const submit = () => {
    if (invalid.length) {
      if (groupInvalid) setGroupOpen(true);
      window.setTimeout(() => document.getElementById(fieldId(invalid[0]))?.focus(), 0);
      return;
    }
    if (!dirty) return;
    if (enabled !== serverEnabled) setConfirming(true);
    else void card.save(changes, clears, { clearFailedMessage: CLEAR_FAILED });
  };

  const groupSummary = groupInvalid
    ? 'Some fields need fixing'
    : `${num(v(K.feePercent))}% + AED ${money(num(v(K.feeFixedAed)))} ${vatIncl ? 'incl. VAT' : '+ 5% VAT'} · paid by ${garagePays
      ? `the garage, on the ${v(K.feeGarageBase) === 'SERVICE_ONLY' ? 'service share' : 'full payment'}` : 'Garagat'} · hold ${num(v(K.settlementHoldDays))} days`;

  const example = () => {
    if (groupInvalid) return <span className="config-dim">Fix the fields above to see the fee.</span>;
    const fee = (500 * num(v(K.feePercent))) / 100 + num(v(K.feeFixedAed));
    const kept = vatIncl
      ? `AED ${money(fee)} (incl. VAT ${money((fee * 5) / 105)})`
      : `AED ${money(fee * 1.05)} (${money(fee)} + VAT ${money(fee * 0.05)})`;
    const who = garagePays
      ? (v(K.feeGarageBase) === 'SERVICE_ONLY'
        ? 'the garage pays it on its service share; Garagat pays it on its delivery fee'
        : 'deducted from the garage payout')
      : 'Garagat absorbs it; the garage payout does not change';
    return <>On a 500 AED Tabby payment: Tabby keeps <b>{kept}</b>, {who}. <span className="config-dim">(example)</span></>;
  };

  const numberField = (key: string, label: string, unit: string, unitBefore: boolean, hint: string) => (
    <NumberField
      id={fieldId(key)}
      label={label}
      unit={unit}
      unitBefore={unitBefore}
      value={v(key)}
      onChange={(val) => card.setField(key, val)}
      hint={hint}
      error={errorOf(key)}
      deployed={deployed(key)}
    />
  );

  const pilotErr = errorOf(K.pilotCustomerIds);

  return (
    <>
      <ConfigCardFrame
        testId="tabby-card"
        title="Tabby"
        status={<span className={`admin-badge ${status.cls}`} data-testid="tabby-status">{status.text}</span>}
        dirty={dirty}
        sub={'"Pay later with Tabby" at checkout, and what Tabby charges us for it.'}
        lastChanged={card.lastChanged}
        onHistory={() => setHistory(true)}
        footNote="Fee and payout settings apply to Tabby payments captured from now on. Live within a minute."
        footError={card.formError}
        invalidCount={invalid.length}
        saving={card.saving}
        canSave={dirty && invalid.length === 0 && !card.forbidden}
        onDiscard={card.discard}
        onSubmit={submit}
        toast={card.toast}
      >
        {card.forbidden && <ForbiddenNotice onReload={() => card.reload()} />}

        <div className="config-section">
          <h4 className="config-section-title">Checkout</h4>
          <ConfigSwitch
            id="tabby-customer-enabled"
            checked={enabled}
            onChange={(on) => card.setField(K.customerEnabled, on ? 'true' : 'false')}
            label={'Offer "Pay later with Tabby" to customers'}
            description="Off hides Tabby everywhere in the app. Tabby payments already made keep working for refunds. You confirm before this goes live."
          />
          <div className="config-grid config-grid-wide" style={{ marginTop: '1.1rem' }}>
            <div className={`config-field${pilotErr ? ' invalid' : ''}`} data-testid={`field-${fieldId(K.pilotCustomerIds)}`}>
              <label htmlFor={fieldId(K.pilotCustomerIds)}>
                Pilot customers <span className="config-hint">(optional)</span>{' '}
                <span className="admin-badge neutral" data-testid="tabby-pilot-count">{countLabel(draftIds.length)}</span>
              </label>
              <textarea
                id={fieldId(K.pilotCustomerIds)}
                className="config-textarea"
                spellCheck={false}
                value={v(K.pilotCustomerIds)}
                aria-invalid={pilotErr ? true : false}
                aria-describedby={`${fieldId(K.pilotCustomerIds)}-hint${pilotErr ? ` ${fieldId(K.pilotCustomerIds)}-err` : ''}`}
                onChange={(e) => card.setField(K.pilotCustomerIds, e.target.value)}
              />
              <div className="config-hint" id={`${fieldId(K.pilotCustomerIds)}-hint`}>
                Customer ids, comma separated. While set, only these customers see Tabby. Leave empty to offer it to every
                customer. Masked in the audit.
                {deployed(K.pilotCustomerIds) && <span className="config-deployed"> Using the deployed value.</span>}
              </div>
              {pilotErr && <div className="config-err" id={`${fieldId(K.pilotCustomerIds)}-err`}>{pilotErr}</div>}
            </div>
            {numberField(K.minOrderAmount, 'Minimum order', 'AED', true,
              `${range(K.minOrderAmount)}. Payable total after offers and promo, incl. delivery. Below it Tabby shows greyed. 0 = no floor.`)}
          </div>
        </div>

        <details className="config-group" open={groupOpen} onToggle={(e) => setGroupOpen((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>
            <span className="config-chev" aria-hidden="true">›</span>
            <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
              <span className="config-group-title">Tabby merchant fee (paid by {garagePays ? 'the garage' : 'Garagat'})</span>
              <span className="config-group-sum" data-testid="tabby-group-summary">{groupSummary}</span>
            </span>
          </summary>
          <div className="config-group-body">
            <div className="config-hint">What Tabby charges per Tabby payment, from our Tabby contract. This is not the customer service fee above.</div>
            <h5>Fee</h5>
            <div className="config-grid">
              {numberField(K.feePercent, 'Percent of each payment', '%', false, `${range(K.feePercent)}. 6.99 means 6.99%.`)}
              {numberField(K.feeFixedAed, 'Fixed per payment', 'AED', true, `${range(K.feeFixedAed)}, on top of the percent.`)}
            </div>
            <ConfigSwitch
              id="tabby-fee-vat-inclusive"
              checked={vatIncl}
              onChange={(on) => card.setField(K.feeVatInclusive, on ? 'true' : 'false')}
              label="Fee already includes 5% VAT"
              description={vatIncl
                ? 'On: the percent and fixed amount above already include 5% VAT.'
                : 'Off (our contract today): Tabby adds 5% VAT on top of the fee.'}
            />
            <h5>Who pays it</h5>
            <div className="config-grid">
              <div className="config-field">
                <label htmlFor={fieldId(K.feeBearer)}>Paid by</label>
                <select
                  id={fieldId(K.feeBearer)}
                  className="config-select"
                  value={v(K.feeBearer)}
                  aria-describedby={`${fieldId(K.feeBearer)}-hint`}
                  onChange={(e) => card.setField(K.feeBearer, e.target.value)}
                >
                  {options(entries.get(K.feeBearer)?.allowedValues, BEARER_LABELS).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <div className="config-hint" id={`${fieldId(K.feeBearer)}-hint`}>
                  Default for every garage. A garage&apos;s own setting overrides it.
                  {deployed(K.feeBearer) && <span className="config-deployed"> Using the deployed value.</span>}
                </div>
              </div>
              <div className="config-field">
                <label htmlFor={fieldId(K.feeGarageBase)}>Garage pays it on</label>
                <select
                  id={fieldId(K.feeGarageBase)}
                  className="config-select"
                  value={v(K.feeGarageBase)}
                  disabled={!garagePays}
                  aria-describedby={`${fieldId(K.feeGarageBase)}-hint`}
                  onChange={(e) => card.setField(K.feeGarageBase, e.target.value)}
                >
                  {options(entries.get(K.feeGarageBase)?.allowedValues, BASE_LABELS).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <div className="config-hint" id={`${fieldId(K.feeGarageBase)}-hint`}>
                  {garagePays
                    ? 'Service share only: Garagat pays the share on its own pickup and delivery fee.'
                    : 'Not used while Garagat pays the fee.'}
                </div>
              </div>
            </div>
            <h5>Payouts</h5>
            <div className="config-grid">
              {numberField(K.settlementHoldDays, 'Settlement hold', 'days', false,
                `${range(K.settlementHoldDays)} days after capture before the order can be paid out to the garage.`)}
              {numberField(K.payoutFeeAed, 'Tabby payout fee', 'AED', true,
                `${range(K.payoutFeeAed)}. Deducted by Tabby from each payout it sends us. Prefills the payout record.`)}
            </div>
            <div className="config-example" style={{ marginTop: 0 }} aria-live="polite" data-testid="tabby-example">
              <span>{example()}</span>
            </div>
          </div>
        </details>
      </ConfigCardFrame>

      {confirming && (
        <ConfirmChangesDialog
          title={enabled ? 'Offer Tabby at checkout?' : 'Hide Tabby at checkout?'}
          body={enabled
            ? `"Pay later with Tabby" appears at checkout ${draftIds.length
              ? `for the ${countLabel(draftIds.length).toLowerCase()} on the pilot list only` : 'for every customer'} within a minute. Needs the Tabby keys deployed on this environment.`
            : 'Tabby disappears from the app within a minute. Tabby payments already made keep working for refunds.'}
          confirmLabel={enabled ? 'Save and turn on' : 'Save and turn off'}
          changes={changeLines()}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); void card.save(changes, clears, { clearFailedMessage: CLEAR_FAILED }); }}
        />
      )}
      {history && (
        <ConfigHistoryDialog
          title="Tabby history"
          keys={KEYS}
          labels={LABELS}
          sensitiveKeys={KEYS.filter((k) => entries.get(k)?.sensitive)}
          maskSensitive
          token={token}
          onClose={() => setHistory(false)}
        />
      )}
    </>
  );
}

function fieldId(key: string): string {
  return `tabby-${key.split('.').pop()}`;
}
